export default {
  // 集成指南页面
  title: "Affiliate Integration Documentation",
  selectPlatform: "Select your website platform",
  helpAndFeedback: "Help & Feedback",
  setupCompleted: "Setup completed",
  noCampaignId: "No campaignId or clientId",

  // 平台选择
  platforms: {
    selfHosted: "Self-hosted",
    shopify: "Shopify",
  },

  // 自托管集成指南
  selfHosted: {
    overview: {
      title: "1. Overview",
      description:
        "This guide walks you through integrating Aha's tracking system to enable precise attribution and seamless revenue sharing across the platform (Aha), influencers (affiliates), and merchants (brands).",
      keyGoals: "Key goals for this guide:",
      goals: [
        "Deliver clear, developer-friendly integration examples.",
        "Support both client-side and server-side implementation workflows.",
        "Ensure accurate attribution for payments and automate revenue distribution.",
      ],
    },
    prerequisites: {
      title: "2. Prerequisites",
      table: {
        item: "Item",
        details: "Details",
      },
      items: {
        clientId: "Client ID",
        apiKey: "API Key (server)",
        apiKeyDesc:
          "Aha API key for server calls (keep it secret!) Generate your key at:",
        orderId: "AHA_ORDER_ID",
        orderIdDesc:
          "This ID is appended to the URL when a user clicks an affiliate link and should be preserved through the payment process to ensure attribution.",
      },
    },
    embedScript: {
      title: "3. Embed the Tracker Script",
      description: "Add this script inside the {headTag} tag of {everyPage}:",
      everyPage:
        "every page involved in the affiliate payment or conversion journey:",
      warning1:
        "⚠️ Please use the official script as-is. Modifying it or changing the client ID may result in failed tracking.",
      warning2:
        "Make sure all landing, payment, and success pages include this script for proper attribution and revenue sharing.",
    },
    integration: {
      title: "4. Integration",
      clientSide: {
        title: "4.1 Client‑Side",
        subtitle: "Example: Track an event on/after purchase",
        description:
          'This should be called either when the user clicks the "Pay" button, or in the callback after a successful payment.',
      },
      serverSide: {
        title: "4.2 Server‑Side Integration",
        subtitle: "Example: Track an event on/after purchase:",
        description:
          "Send tracking events directly from your backend for better stability and control.",
        button:
          "This should be called either when the user clicks the “Pay” button, or in the callback after a successful payment.",
      },
    },
    testing: {
      title: "5. Testing & Verification",
      steps: [
        "After correctly integrating the setup, append the following query parameters after your landing page URL: ?AHA_ORDER_ID=test_order_for_integration&AHA_CAMPAIGN_ID={campaignId}&AHA_CODE=test_code&utm_source=Aha",
        "In your code, simulate the purchase tracking event as demonstrated in the sample integration.",
      ],
      verifyButton: "Verify integration",
      integrationSuccessful: "Integration successful",
      integrationNotDetected: "Integration not detected",
    },
    troubleshooting: {
      title: "6. Troubleshooting",
      table: {
        symptom: "Symptom",
        causeAndFix: "Cause & Fix",
      },
      issues: {
        noEvents: {
          symptom: "No events sent",
          cause:
            "Script missing, client ID invalid, or {trackFunction} not called.",
        },
        trackerUndefined: {
          symptom: "{tracker} undefined",
          cause: "Script hasn't loaded — wrap in {onloadFunction}.",
        },
        redirectLosesParams: {
          symptom: "Redirect loses tracking params",
          cause: "Use same-domain pages or switch to server-side integration.",
        },
        apiError: {
          symptom: "401/403 on server API",
          cause: "Incorrect/missing {authHeader} header or API key.",
        },
        paymentNotAttributed: {
          symptom: "Payment not attributed to affiliate",
          cause:
            "Check that the affiliate params and AHA_ORDER_ID are correctly passed through all steps.",
        },
        revenueNotSplit: {
          symptom: "Revenue not split as expected",
          cause:
            "Ensure all parties and split rules are correctly configured in the Aha platform.",
        },
      },
    },
  },

  // Shopify集成指南
  shopify: {
    overview: {
      title: "1. Overview",
      description:
        "This guide will help you integrate Aha's tracking system with Shopify to enable accurate attribution, seamless revenue sharing, and automated payouts between merchants and affiliates.",
    },
    prerequisites: {
      title: "2. Prerequisites",
      table: {
        item: "Item",
        details: "Details",
      },
      items: {
        clientId: "Your client ID",
        apiKey: "API Key (server)",
        apiKeyDesc:
          "Aha API key for server calls (keep it secret!) Generate your key at:",
        orderId: "AHA_ORDER_ID",
        orderIdDesc:
          "This ID is appended to the URL when a user clicks an affiliate link and should be preserved through the payment process to ensure attribution.",
      },
    },
    embedScript: {
      title: "3. Embed the Tracker Script",
      steps: [
        "Log in to your Shopify admin panel.",
        "Open the Theme Editor: Go to Online Store → Themes → Actions → Edit code.",
        "Edit the layout file:",
        "Save your changes.",
        "Verify:",
      ],
      subSteps: {
        openLayout: "Open layout/theme.liquid.",
        pasteCode: "Paste the following code right before the </head> tag:",
        checkScript:
          "Open any page of your store and check if the track script is loaded in the console.",
        checkNetwork:
          "Check the Network panel to see if the script is being correctly requested.",
      },
    },
    salesDataReporting: {
      title: "4. Sales Data Reporting",
      steps: [
        "Log in to your Shopify admin panel.",
        "Go to Settings → Customer events → Add custom pixel.",
        "Create a custom pixel and paste the following code.",
      ],
    },
    testAndVerify: {
      title: "5. Test and Verify",
      steps: [
        "Enable test mode:",
        "Append the following query parameters after your landing page URL: ?AHA_ORDER_ID=test_order_for_integration&AHA_CAMPAIGN_ID={campaignId}&AHA_CODE=test_code&utm_source=Aha",
        "Use the following payment information to make a purchase",
        'Click "Verify integration" below to confirm whether the integration has been successful.',
      ],
      subSteps: {
        goToAdmin: "Go to your Shopify admin.",
        navigateToPayments: "Navigate to Settings > Payments.",
        clickManage: "Click Manage.",
        enableTestMode: "Enable Test mode.",
      },
      verifyButton: "Verify integration",
      integrationSuccessful: "Integration successful",
      integrationNotDetected: "Integration not detected",
    },
  },
};
